import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Grid,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CustomCheckbox from '../../../common/components/CustomCheckbox/CustomCheckbox';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import {
  MainContent,
  ReportContainer,
  SectionTitle,
  CheckboxGroup,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
} from './CN61CompoundRateReports.styled';

interface DirectionOption {
  id: string;
  label: string;
}

interface CN61ReportFilters {
  ppoValue: string;
  direction: string;
  year: string;
  includeCrrCn61Upu: boolean;
}

// Generate year options from 2008 to current year
const generateYearOptions = () => {
  const options: { value: string; label: string }[] = [];
  const currentYear = new Date().getFullYear();

  for (let year = currentYear; year >= 2008; year--) {
    options.push({
      value: year.toString(),
      label: year.toString(),
    });
  }

  return options;
};

function CN61CompoundRateReports() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();

  // Filter states
  const [filters, setFilters] = useState<CN61ReportFilters>({
    ppoValue: '',
    direction: 'All',
    year: '',
    includeCrrCn61Upu: false,
  });

  // Get effective operators + cascading logic for e53 module (UPU TD Accounting)
  const {
    operators,
    availableYears,
    operatorsForYear,
    yearsForOperator,
    relationsData: allRelationsData,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('e53', { selectedYear: filters.year, selectedPpo: filters.ppoValue });

  // Generate year options with cascading logic
  const yearOptions = useMemo(() => {
    const yearsToShow = filters.ppoValue ? yearsForOperator : availableYears;
    return yearsToShow.map(year => ({ value: year.toString(), label: year.toString() }));
  }, [availableYears, yearsForOperator, filters.ppoValue]);

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'All', label: 'All' },
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/uputdaccounting');
  };

  const handleDirectionChange = (id: string) => {
    setFilters((prev) => ({ ...prev, direction: id }));
  };

  const handlePpoChange = (value: string) => {
    setFilters((prev) => ({ ...prev, ppoValue: value }));
  };

  const handleYearChange = (value: string) => {
    setFilters((prev) => {
      // If DO is selected and not available for the new year, clear DO
      if (prev.ppoValue) {
        const relationsForNewYear = allRelationsData?.byYear?.[value];
        const isCurrentDOAvailableForNewYear = relationsForNewYear?.some(relation =>
          relation.targetDO === prev.ppoValue &&
          relation.modules.some(module => module.name === 'e53' && module.enabled === true)
        );
        if (!isCurrentDOAvailableForNewYear) {
          return { ...prev, year: value, ppoValue: '' };
        }
      }
      return { ...prev, year: value };
    });
  };

  const handleIncludeCrrChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setFilters((prev) => ({ ...prev, includeCrrCn61Upu: isChecked }));
  };

  const handleReset = () => {
    setFilters({
      ppoValue: '',
      direction: 'Inbound',
      year: '',
      includeCrrCn61Upu: false,
    });
  };

  const handleEnterTdRates = () => {
    // Validate required fields
    if (!filters.year) {
      alert('Please select a Year');
      return;
    }

    if (!filters.ppoValue) {
      alert('Please select a PPO');
      return;
    }

    // Build the query parameters to pass to the subpage
    const queryParams = new URLSearchParams({
      ppo: filters.ppoValue,
      year: filters.year,
      direction: filters.direction,
    });

    // Redirect to appropriate subpage based on CRR inclusion
    if (filters.includeCrrCn61Upu) {
      // Redirect to CRR page when Include CRR is checked
      navigate(`/cn61-compound-rate-reports/upu/crr?${queryParams.toString()}`);
    } else {
      // Redirect to UPU page
      navigate(`/cn61-compound-rate-reports/upu?${queryParams.toString()}`);
    }
  };

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column' }}
      data-testid='cn61-upu-compound-rate-reports-container'
    >
      <Grid container data-testid="cn61-upu-compound-rate-reports-grid-container">
        {/* First Row - SectionBar */}
        <Grid item xs={12} data-testid="cn61-upu-compound-rate-reports-section-bar-container">
          <SectionBar
            title='CN61 & Compound Rate Reports'
            subtitle='UPU TD Accounting'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            module='e53'
            data-testid='cn61-upu-compound-rate-reports-section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }} data-testid="cn61-upu-compound-rate-reports-main-row">
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }} data-testid="cn61-upu-compound-rate-reports-sidebar-container">
              <Sidebar
                isOpen={sidebarOpen}
                onToggle={handleSidebarToggle}
                navItems={navItems}
                data-testid='cn61-upu-compound-rate-reports-sidebar'
              />
            </Box>
          )}

          {/* Main Content */}
          <MainContent
            sidebarOpen={sidebarOpen}
            data-testid='cn61-upu-compound-rate-reports-main-content'
          >
            <ReportContainer data-testid='cn61-upu-compound-rate-reports-content'>
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
                data-testid='cn61-upu-compound-rate-reports-header'
              >
                <Typography
                  variant='h4'
                  sx={{ fontWeight: 600, color: '#063142' }}
                  data-testid='cn61-upu-compound-rate-reports-title'
                >
                  CN61 & Compound Rate Reports
                </Typography>
              </Box>

              {/* Main Filter Section - Always visible */}
              <>
                {/* PPO Section */}
                <FilterSection data-testid="cn61-upu-compound-rate-reports-filter-section">
                  {/* PPO Dropdown and Direction Filter in one row */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }} data-testid="cn61-upu-compound-rate-reports-ppo-direction-row">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="cn61-upu-compound-rate-reports-ppo-container">
                      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }} data-testid="cn61-upu-compound-rate-reports-ppo-label">
                        DO *
                      </Typography>
                      <SelectContainer data-testid="cn61-upu-compound-rate-reports-ppo-select-container">
                        <FormControl size='small' fullWidth data-testid="cn61-upu-compound-rate-reports-ppo-form-control">
                          <Select
                            value={filters.ppoValue}
                            onChange={(e) => handlePpoChange(e.target.value)}
                            displayEmpty
                            sx={{ minWidth: 160 }}
                            disabled={isPpoLoading}
                            data-testid="cn61-upu-compound-rate-reports-ppo-select"
                          >
                            <MenuItem value='' data-testid="cn61-upu-compound-rate-reports-ppo-default-option">
                              {isPpoLoading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} data-testid="cn61-upu-compound-rate-reports-ppo-loading">
                                  <CircularProgress size={16} data-testid="cn61-upu-compound-rate-reports-ppo-loading-spinner" />
                                  <span data-testid="cn61-upu-compound-rate-reports-ppo-loading-text">Loading...</span>
                                </Box>
                              ) : (
                                'All'
                              )}
                            </MenuItem>
                            {!isPpoLoading &&
                              !ppoError &&
                              (filters.year ? operatorsForYear : operators).map((targetDO) => (
                                <MenuItem key={targetDO} value={targetDO} data-testid={`cn61-upu-compound-rate-reports-ppo-option-${targetDO}`}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      width: '100%',
                                    }}
                                    data-testid={`cn61-upu-compound-rate-reports-ppo-option-content-${targetDO}`}
                                  >
                                    <span data-testid={`cn61-upu-compound-rate-reports-ppo-option-label-${targetDO}`}>{targetDO}</span>
                                   
                                  </Box>
                                </MenuItem>
                              ))}
                            {ppoError && <MenuItem disabled data-testid="cn61-upu-compound-rate-reports-ppo-error">Error loading PPOs</MenuItem>}
                          </Select>
                        </FormControl>
                      </SelectContainer>
                    </Box>

                    {/* Direction Filter using Segmented Control */}
                    <StyledSegmentedControl data-testid="cn61-upu-compound-rate-reports-direction-control">
                      {directionOptions.map((option) => (
                        <StyledSegmentButton
                          key={option.id}
                          active={filters.direction === option.id}
                          onClick={() => handleDirectionChange(option.id)}
                          sx={{
                            color: filters.direction === option.id ? '#ffffff' : '#00B6BA',
                            minWidth: '70px',
                          }}
                          data-testid={`cn61-upu-compound-rate-reports-direction-${option.id}`}
                        >
                          {option.label}
                        </StyledSegmentButton>
                      ))}
                    </StyledSegmentedControl>
                  </Box>

                  {/* Divider */}
                  <Box
                    sx={{
                      height: '1px',
                      backgroundColor: '#E1E8ED',
                      mb: 2,
                    }}
                    data-testid="cn61-upu-compound-rate-reports-divider-1"
                  />

                  {/* Year Selector */}
                  <Box sx={{ display: 'flex', gap: 2 }} data-testid="cn61-upu-compound-rate-reports-year-section">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="cn61-upu-compound-rate-reports-year-container">
                      <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }} data-testid="cn61-upu-compound-rate-reports-year-label">
                        Year: *
                      </Typography>
                      <SelectContainer data-testid="cn61-upu-compound-rate-reports-year-select-container">
                        <FormControl size='small' fullWidth data-testid="cn61-upu-compound-rate-reports-year-form-control">
                          <Select
                            value={filters.year}
                            onChange={(e) => handleYearChange(e.target.value)}
                            displayEmpty
                            sx={{ minWidth: 184 }}
                            data-testid="cn61-upu-compound-rate-reports-year-select"
                          >
                            <MenuItem value='' data-testid="cn61-upu-compound-rate-reports-year-default-option">Select Year</MenuItem>
                            {(filters.ppoValue ? yearsForOperator : availableYears).map((year) => (
                              <MenuItem key={year} value={year.toString()} data-testid={`cn61-upu-compound-rate-reports-year-option-${year}`}>
                                {year}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </SelectContainer>
                    </Box>
                  </Box>

                  {/* Divider */}
                  <Box
                    sx={{
                      height: '1px',
                      backgroundColor: '#E1E8ED',
                      mb: 2,
                    }}
                    data-testid="cn61-upu-compound-rate-reports-divider-2"
                  />

                  {/* CN61 Options */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      width: '100%',
                      gap: 2,
                    }}
                    data-testid="cn61-upu-compound-rate-reports-options-section"
                  >
                    {/* CN61 UPU */}
                    <Box
                      sx={{
                        flex: { xs: 'none', md: '1 1 0' },
                        padding: 2,
                        minWidth: 0,
                      }}
                      data-testid="cn61-upu-compound-rate-reports-upu-section"
                    >
                      <Button
                        variant='outlined'
                        size='small'
                        onClick={handleEnterTdRates}
                        disabled={!filters.year || !filters.ppoValue}
                        sx={{
                          borderColor: '#00ACE7',
                          color: '#00ACE7',
                          fontSize: '12px',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          padding: '6px 16px',
                          mb: 2,
                          '&:hover': {
                            borderColor: '#0095C7',
                            backgroundColor: '#E3F7FE',
                          },
                          '&:disabled': {
                            borderColor: '#ccc',
                            color: '#ccc',
                          },
                        }}
                        data-testid="cn61-upu-compound-rate-reports-enter-td-rates-btn"
                      >
                        Enter TD Rates
                      </Button>
                      <CheckboxGroup data-testid="cn61-upu-compound-rate-reports-checkbox-group">
                        <CustomCheckbox
                          checked={filters.includeCrrCn61Upu}
                          onChange={handleIncludeCrrChange}
                          label='Include CRR'
                          data-testid="cn61-upu-compound-rate-reports-include-crr-checkbox"
                        />
                      </CheckboxGroup>
                    </Box>

                  </Box>

                </FilterSection>
              </>

            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

    </Box>
  );
}

export default CN61CompoundRateReports;
